import { tw } from "@careinfo/tailwind";
import { Combobox, Transition } from "@headlessui/react";
import { ChevronDownIcon, PlusIcon } from "@heroicons/react/24/outline";
import { Fragment, useEffect, useRef, useState } from "react";
import { useController } from "react-hook-form";
import { classNames } from "../../../utils/classNames";

export default function LocalCombobox(props: any) {
  const { name, clearErrors, disabled = false, data, control } = props;

  const {
    field: { value, onChange },
    fieldState: { error },
  } = useController({
    control,
    name,
  });

  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<{ id: string; name: string }>({
    id: value ?? null,
    name: value ?? null,
  });
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const comboboxRef = useRef<HTMLDivElement | null>(null);

  const filtereddata: any =
    query === ""
      ? data
      : data.filter((person: any) =>
          person?.name
            .toLowerCase()
            .replace(/\s+/g, "")
            .includes(query.toLowerCase().replace(/\s+/g, ""))
        );

  const handleChange = (v) => {
    setSelected(v);
    onChange(v.id);
  };

  useEffect(() => {
    const controller = new AbortController();
    document.addEventListener(
      "mousedown",
      (event) => {
        if (
          comboboxRef.current &&
          !comboboxRef.current.contains(event.target as Node)
        ) {
          setIsOpen(false);
        }
      },
      {
        signal: controller.signal,
      }
    );
    document.addEventListener(
      "focusin",
      () => {
        if (
          comboboxRef.current &&
          !comboboxRef.current.contains(document.activeElement as Node)
        ) {
          setIsOpen(false);
        }
      },
      {
        signal: controller.signal,
      }
    );

    return () => {
      controller.abort();
    };
  }, [comboboxRef]);

  return (
    <>
      <Combobox value={selected} disabled={disabled} onChange={handleChange}>
        <div ref={comboboxRef} className={tw`relative mt-3`}>
          <label>{props?.label}</label>

          <div
            className={tw`relative flex items-center bg-[#EEF5F4] w-full cursor-default overflow-hidden  bg-white text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-teal-300 sm:text-sm mt-2`}
          >
            <Combobox.Input
              className={tw`w-full focus:outline-none bg-transparent  p-[10px] text-left`}
              placeholder={props?.placeholder}
              onBlur={() => clearErrors(name)}
              displayValue={(person: any) => person?.name}
              onChange={(event) => setQuery(event.target.value)}
              onClick={() => setIsOpen(true)}
            />
            <div className={tw`px-2 flex items-center gap-1`}>
              {selected && (
                <Combobox.Button
                  className={tw`flex items-center pr-2`}
                  onClick={(e) => {
                    e.stopPropagation(); // Pjjrevent Combobox from opening
                    setSelected({ id: null, name: null }); // Clear selection
                    onChange(null); // Reset form value
                  }}
                >
                  <PlusIcon
                    className={classNames(tw`h-5 w-5 text-gray-400 rotate-45`)}
                    aria-hidden="true"
                  />
                </Combobox.Button>
              )}
              <Combobox.Button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsOpen(!isOpen);
                }}
              >
                <ChevronDownIcon
                  className={tw`h-5 w-5 text-gray-400 ${
                    isOpen && "rotate-[180deg]"
                  }`}
                  aria-hidden="true"
                />
              </Combobox.Button>
            </div>
          </div>
          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            show={isOpen}
            afterLeave={() => setQuery("")}
          >
            <Combobox.Options
              className={tw`absolute space-y-2  z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm`}
            >
              {filtereddata?.length === 0 && query !== "" ? (
                <div
                  className={tw`relative cursor-default select-none py-2 px-4 text-gray-700`}
                >
                  Nothing found.
                </div>
              ) : (
                filtereddata.map((item: any) => (
                  <Combobox.Option
                    key={item.id}
                    className={({ active }) =>
                      tw`relative cursor-default select-none py-2 pl-10 pr-4 hover:bg-primary hover:text-white hover:cursor-pointer ${
                        active ? tw`bg-primary text-white` : tw`text-gray-900`
                      }`
                    }
                    value={item}
                    onClick={() => setIsOpen(false)}
                  >
                    {({ selected, active }) => (
                      <>
                        <span
                          className={tw`block truncate ${
                            selected ? tw`font-medium` : tw`font-normal`
                          }`}
                        >
                          {item.name}
                        </span>
                        {selected ? (
                          <span
                            className={tw`absolute inset-y-0 left-0 flex items-center pl-3 ${
                              active ? tw`text-white` : tw`text-teal-600`
                            }`}
                          >
                            {/* <CheckIcon className="h-5 w-5" aria-hidden="true" /> */}
                          </span>
                        ) : null}
                      </>
                    )}
                  </Combobox.Option>
                ))
              )}
            </Combobox.Options>
          </Transition>
          {error && (
            <p className={tw`text-red-500 text-xs my-1`}>{error?.message}</p>
          )}
        </div>
      </Combobox>
    </>
  );
}
